import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const lidingoPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-lidingo",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Lidingö – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling på Lidingö. Vi hämtar möbler, vitvaror och skräp i Lidingö centrum, Larsberg, Käppala, Bodal, Dalénum och Brevik. Fast pris, ofta samma dag.",
      h1: "Bortforsling Lidingö",
    },

    hero: {
      heading: "Bortforsling på Lidingö",
      intro:
        "Boka bortforsling på Lidingö – vi hämtar möbler, vitvaror och skrymmande föremål över hela ön, från villorna i Brevik, Käppala och Skärsätra till lägenheterna i Larsberg, Bodal och nya Dalénum. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling på Lidingö – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar på Lidingö",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling på Lidingö och körs till Stockby Returpark",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Brevik, Lidingö",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Larsberg, Lidingö",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Dalénum, Lidingö",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Käppala, Lidingö",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Bodal, Lidingö",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling på Lidingö – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Lidingö är en ö och en egen stad med 28 kommundelar – från de gröna villakvarteren i Brevik, Käppala, Skärsätra och Sticklinge till de tätare lägenhetsområdena i Larsberg, Baggeby, Bodal och nya Dalénum på det gamla AGA-området. Den blandningen gör bortforsling till olika utmaningar: i villorna handlar det ofta om att tömma garage, sjöbod eller källare, medan det i lägenheterna handlar om trapphus och hiss. Och eftersom Lidingö är en ö måste allt över Lidingöbron – men det är just sådant vi löser åt dig. Med Ungdomskraft slipper du allt krångel. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress på ön.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din villa, lägenhet eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Här har Lidingö en stor fördel – Stockby Returpark vid Stockbyvägen ligger på ön och drivs av SÖRAB, som Lidingö är delägare i. Det betyder att avfallet stannar lokalt och inte behöver köras över bron. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att vi planerar rutten med hänsyn till Lidingöbron och Lidingöbanan – som binder ihop ön från Ropsten via Torsvik, Larsberg, Aga och Skärsätra ut till Gåshaga – tar vi oss smidigt mellan kommundelarna. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både de stora villatomterna i Elfvik och Brevik och de smala vägarna i Hersby – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling på Lidingö",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Brevik, Käppala, Skärsätra eller Sticklinge är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Larsberg, Bodal eller Dalénum, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller sjöboden. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Lidingöbo på Stockby Returpark vid Stockbyvägen.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker på Lidingö",
          content:
            "Vi utför bortforsling över hela Lidingö – från de gröna villaöarna till de tätare lägenhetsområdena. Oavsett om du bor i en villa i Brevik, ett hus i Käppala, en lägenhet i Larsberg eller en nybyggd etta i Dalénum kan vi vara på plats, ofta redan samma dag.\n\nI de centrala delarna – Lidingö centrum, Torsvik, Baggeby och Bodal – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära centrum och Lidingöbanan. I Larsberg möter vi den klassiska sextiotalsbebyggelsen med höga hus och stora gårdar, och i Dalénum de nya kvarteren på det gamla AGA-området vid vattnet.\n\nVi är minst lika snabba i öns östra och norra delar: villaområdena i Käppala, Skärsätra, Brevik och Gåshaga ut mot skärgården, samt Rudboda, Sticklinge, Elfvik och Hersby. Tack vare att vi planerar rutten över Lidingöbron och längs Norra och Södra Kungsvägen når vi alla kommundelar utan problem. Inga extra avgifter för någon del av ön.",
        },
        {
          heading: "Återvinning och miljöansvar på Lidingö",
          content:
            "En viktig del av vår bortforsling på Lidingö är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. Lidingö har en stor fördel eftersom kommunen är delägare i SÖRAB och har en egen station på ön:\n\nStockby Returpark vid Stockbyvägen – Lidingös egen återvinningscentral, med stort fokus på återbruk, där sorterat grovavfall, elavfall och farligt avfall tas emot liksom föremål och textil för återanvändning. Eftersom Stockby ligger på ön behöver avfallet inte köras över bron, vilket håller restiden – och därmed kostnaden – nere. Större mängder trädgårdsavfall körs till Södergarn.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling på Lidingö med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Lidingös kommundelar, från villatomterna i Brevik och Elfvik till lägenheterna i Larsberg och Dalénum, och vet att Stockby Returpark ligger på ön. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och tar oss smidigt över Lidingöbron. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för att vi kör över bron, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Stockby Returpark och SÖRAB:s anläggningar. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling på Lidingö?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för att vi kör över Lidingöbron.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling på Lidingö?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling på Lidingö?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och tar oss smidigt över Lidingöbron, så vi når alla kommundelar snabbt. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling på Lidingö?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Hämtar ni från villa, garage och sjöbod på Lidingö?",
        answer:
          "Ja, en stor del av våra uppdrag på Lidingö är villahämtningar i Brevik, Käppala, Skärsätra och Elfvik. Vi backar in på uppfarten där det går och tömmer garage, källare, vind och sjöbod. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa på Lidingö?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Larsberg, Bodal, Baggeby och Dalénum. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen på Lidingö försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling på Lidingö?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Lidingö har en egen SÖRAB-station, Stockby Returpark vid Stockbyvägen, så avfallet stannar på ön och behöver inte köras över bron. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Tar ni extra betalt för att köra över Lidingöbron?",
        answer:
          "Nej. Vårt fasta pris bygger på volym, vikt och tid – det finns inget tillägg för att vi kör över Lidingöbron eller för att din adress ligger på ön. Du ser hela priset direkt vid bokning.",
      },
      {
        question: "Kan ni hämta saker från källare, vind eller sjöbod på Lidingö?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, vindsutrymmen, sjöbodar, förråd och garage. Vi är vana vid de stora villaförråden i Brevik och Elfvik såväl som lägenhetsförråden i Larsberg.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling på Lidingö?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din villa i Käppala och ditt förråd på en annan adress på ön.",
      },
      {
        question: "Vilka kommundelar på Lidingö täcker ni?",
        answer:
          "Vi utför bortforsling över hela Lidingö – Lidingö centrum, Torsvik, Larsberg, Baggeby, Bodal, Käppala, Skärsätra, Brevik, Gångsätra, Rudboda, Sticklinge, Elfvik, Hersby, Gåshaga och Dalénum. Inga extra avgifter för någon del av ön.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling på Lidingö?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Lidingöbo på Stockby Returpark vid Stockbyvägen.",
      },
      {
        question: "Vilka kommuner runt Lidingö utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling på Lidingö i 3 enkla steg",
      step2Text:
        "Våra ungdomar kommer till dig på Lidingö och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling på Lidingö idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling över hela Lidingö.",
    },

    en: {
      slug: "junk-removal-lidingo",
      meta: {
        title: "Junk Removal Lidingö – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal on Lidingö. We pick up furniture, appliances and bulky items in Lidingö centrum, Larsberg, Käppala, Bodal, Dalénum and Brevik. Fixed price, often same day.",
        h1: "Junk Removal Lidingö",
      },
      hero: {
        heading: "Junk Removal on Lidingö",
        intro:
          "Book junk removal on Lidingö – we collect furniture, appliances and bulky items across the whole island, from the villas in Brevik, Käppala and Skärsätra to the apartments in Larsberg, Bodal and the new Dalénum. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal on Lidingö – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove on Lidingö",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal on Lidingö and driven to Stockby Returpark",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Brevik, Lidingö",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Larsberg, Lidingö",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Dalénum, Lidingö",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Käppala, Lidingö",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Bodal, Lidingö",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal on Lidingö – fast, hassle-free and responsible",
        mainText:
          "Lidingö is an island and a city of its own with 28 districts – from the green villa neighbourhoods of Brevik, Käppala, Skärsätra and Sticklinge to the denser apartment areas of Larsberg, Baggeby, Bodal and the new Dalénum on the old AGA site. That mix makes junk removal a different challenge: in the villas it is often about clearing a garage, boathouse or basement, while in the apartments it is about stairwells and elevators. And since Lidingö is an island, everything has to cross the Lidingö bridge – but that is exactly what we take care of for you. With Ungdomskraft, you skip the hassle entirely. We send young, strong workers with a van directly to your address on the island.\n\nOur team handles the entire chain: we carry items out of your villa, apartment or storage unit, load them into the van and drive to the nearest recycling centre. Here Lidingö has a big advantage – Stockby Returpark on Stockbyvägen is located on the island and run by SÖRAB, of which Lidingö is a part-owner. This means the waste stays local and doesn't have to be driven across the bridge. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause we plan the route with the Lidingö bridge and the Lidingöbanan light rail in mind – which links the island from Ropsten via Torsvik, Larsberg, Aga and Skärsätra out to Gåshaga – we move smoothly between districts. This means we can often be on site the same day you book. Our workers are used to both the large villa plots of Elfvik and Brevik and the narrow roads of Hersby – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal on Lidingö",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Brevik, Käppala, Skärsätra or Sticklinge, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Larsberg, Bodal or Dalénum, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or boathouse. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Lidingö resident you can drop those off for free at Stockby Returpark on Stockbyvägen.",
        additionalSections: [
          {
            heading: "Districts and areas we cover on Lidingö",
            content:
              "We perform junk removal across the whole of Lidingö – from the green villa parts of the island to the denser apartment areas. Whether you live in a villa in Brevik, a house in Käppala, an apartment in Larsberg or a new studio in Dalénum, we can be on site, often the same day.\n\nIn the central parts – Lidingö centrum, Torsvik, Baggeby and Bodal – we are highly experienced with apartment pickups via elevator and stairwell, close to the centre and the Lidingöbanan. In Larsberg we meet the classic 1960s housing with tall buildings and large courtyards, and in Dalénum the new quarters on the old AGA site by the water.\n\nWe are just as quick in the eastern and northern parts of the island: the villa areas of Käppala, Skärsätra, Brevik and Gåshaga out towards the archipelago, and Rudboda, Sticklinge, Elfvik and Hersby. Because we plan the route across the Lidingö bridge and along Norra and Södra Kungsvägen, we reach every district without issue. No extra charges for any part of the island.",
          },
          {
            heading: "Recycling and environmental responsibility on Lidingö",
            content:
              "An important part of our junk removal on Lidingö is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Lidingö has a big advantage because the municipality is a part-owner of SÖRAB and has its own station on the island:\n\nStockby Returpark on Stockbyvägen – Lidingö's own recycling centre, with a strong focus on reuse, which receives sorted bulky waste, e-waste and hazardous waste as well as items and textiles for reuse. Because Stockby is on the island, the waste doesn't have to be driven across the bridge, which keeps travel time – and therefore the cost – down. Larger amounts of garden waste go to Södergarn.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal on Lidingö with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Lidingö's districts, from the villa plots of Brevik and Elfvik to the apartments in Larsberg and Dalénum, and that Stockby Returpark is on the island. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and cross the Lidingö bridge smoothly. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharge for crossing the bridge, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Stockby Returpark and SÖRAB's facilities. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal on Lidingö cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharge for crossing the Lidingö bridge.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal on Lidingö?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal on Lidingö?",
          answer:
            "We have vans and staff across the Stockholm region and cross the Lidingö bridge smoothly, so we reach every district quickly. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal on Lidingö?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Do you collect from villas, garages and boathouses on Lidingö?",
          answer:
            "Yes, a large share of our jobs on Lidingö are villa pickups in Brevik, Käppala, Skärsätra and Elfvik. We reverse onto the driveway where possible and clear garages, basements, attics and boathouses. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you carry items down from an apartment via stairs on Lidingö?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Larsberg, Bodal, Baggeby and Dalénum. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal on Lidingö insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal on Lidingö?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Lidingö has its own SÖRAB station, Stockby Returpark on Stockbyvägen, so the waste stays on the island and doesn't have to be driven across the bridge. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Do you charge extra for crossing the Lidingö bridge?",
          answer:
            "No. Our fixed price is based on volume, weight and time – there is no surcharge for crossing the Lidingö bridge or for your address being on the island. You see the full price immediately when booking.",
        },
        {
          question: "Can you collect items from basements, attics or boathouses on Lidingö?",
          answer:
            "Yes, we pick up items from all spaces – basements, attics, boathouses, storage rooms and garages. We are used to the large villa storage rooms in Brevik and Elfvik as well as the apartment storage units in Larsberg.",
        },
        {
          question: "Can you make multiple stops during junk removal on Lidingö?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your villa in Käppala and a storage unit at another address on the island, for example.",
        },
        {
          question: "Which districts on Lidingö do you cover?",
          answer:
            "We perform junk removal across all of Lidingö – Lidingö centrum, Torsvik, Larsberg, Baggeby, Bodal, Käppala, Skärsätra, Brevik, Gångsätra, Rudboda, Sticklinge, Elfvik, Hersby, Gåshaga and Dalénum. No extra charges for any part of the island.",
        },
        {
          question: "Do you handle hazardous waste during junk removal on Lidingö?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Lidingö resident you can drop those off for free at Stockby Returpark on Stockbyvägen.",
        },
        {
          question: "Which municipalities around Lidingö do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal on Lidingö in 3 easy steps",
        step2Text:
          "Our young workers come to you on Lidingö and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal on Lidingö today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole of Lidingö.",
      },
    },
  },
];
